import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Plus, Store, User, Trash2, Loader2, Filter, ClipboardList, Euro, Calendar as CalendarIcon, RefreshCw, Cloud, CheckCircle2, AlertTriangle, MapPin, History, Sparkles, Route, CheckCheck, X, Link2, RotateCcw, Copy, ShieldCheck, Search, Stethoscope, MoreVertical, LifeBuoy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { frenchDate } from "@/lib/shariti";
import { BULLETIN_STATUTS } from "@/components/BulletinsDialog";

const STATUT_TONE = {
  "Approuvé": "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
  "En attente": "bg-[rgba(252,219,83,0.4)] text-[#5A4200]",
  "Invalide": "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
  "Annulé": "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
};
const SOURCE_LABEL = { manuel: "Manuel", evergiving_import: "Evergiving", api: "Reporting", csv: "CSV" };

const _pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
const today = () => ymd(new Date());

// Préréglages de période pour le filtre Bulletins
const PERIODE_PRESETS = {
  ce_mois: {
    label: "Ce mois-ci",
    range: () => { const d = new Date(); return { from: ymd(new Date(d.getFullYear(), d.getMonth(), 1)), to: today() }; },
  },
  mois_dernier: {
    label: "Mois dernier",
    range: () => {
      const d = new Date();
      const from = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const to = new Date(d.getFullYear(), d.getMonth(), 0);
      return { from: ymd(from), to: ymd(to) };
    },
  },
  j30: {
    label: "30 derniers jours",
    range: () => { const d = new Date(); const from = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 29); return { from: ymd(from), to: today() }; },
  },
  cette_annee: {
    label: "Cette année",
    range: () => { const d = new Date(); return { from: ymd(new Date(d.getFullYear(), 0, 1)), to: today() }; },
  },
  tout: { label: "Tout l'historique", range: () => ({ from: "", to: "" }) },
};
const DEFAULT_PRESET = "tout";

export default function Bulletins() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const initialRange = PERIODE_PRESETS[DEFAULT_PRESET].range();
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [filters, setFilters] = useState({ spot: null, rd: null, source: "api", dateFrom: initialRange.from, dateTo: initialRange.to });
  const [showNew, setShowNew] = useState(false);
  const [repStatus, setRepStatus] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lieuxOpen, setLieuxOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  // Audit / nettoyage des doublons de bulletins
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [purgeExcel, setPurgeExcel] = useState(false);

  const load = useCallback(async () => {
    const params = { limit: 2000 };
    if (filters.spot) params.spot_id = filters.spot.id;
    if (filters.rd) params.rd_id = filters.rd.id;
    if (filters.source !== "all") params.source = filters.source;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    const { data } = await api.get("/bulletins", { params });
    setRows(data.bulletins); setTotal(data.total);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const loadRepStatus = useCallback(() => {
    api.get("/bulletins/sync/reporting/status").then((r) => setRepStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadRepStatus(); }, [loadRepStatus]);

  // Aperçu (dry-run : rien n'est écrit) puis confirmation.
  const openPreview = async () => {
    setPreviewOpen(true); setPreview(null); setPreviewLoading(true);
    try {
      const { data } = await api.post("/bulletins/sync/reporting", { dry_run: true });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu impossible.");
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };
  const confirmSync = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/bulletins/sync/reporting", {});
      toast.success(`Import : ${(data.created || 0).toLocaleString("fr-FR")} créés, ${(data.updated || 0).toLocaleString("fr-FR")} mis à jour${data.unresolved_spot ? ` · ${data.unresolved_spot} à rattacher` : ""}.`);
      setPreviewOpen(false);
      loadRepStatus(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible.");
    } finally { setConfirming(false); }
  };

  // Correctif INSTANTANÉ des réattributions Evergiving (ex. bulletins saisis sur la
  // tablette d'un collègue puis corrigés à la source) : balaye les 120 derniers jours,
  // réaligne le recruteur de chaque bulletin sur Reporting (source de vérité) + miroir
  // de la fenêtre paie. Sans attendre le cycle automatique.
  const [reassigning, setReassigning] = useState(false);
  const doReassignSweep = async () => {
    setReassigning(true);
    const t = toast.loading("Correction des attributions en cours… (Reporting = source de vérité)");
    try {
      const { data } = await api.post("/bulletins/sync/reporting/reassign-sweep", {});
      try { await api.post("/bulletins/sync/reporting/mirror", {}); } catch (_) { /* best-effort */ }
      const n = data?.rd_reassigned || 0;
      toast.success(
        n > 0
          ? `${n} bulletin(s) réattribué(s) au bon collaborateur. Les compteurs sont à jour.`
          : "Attributions déjà alignées sur Reporting — rien à corriger.",
        { id: t });
      loadRepStatus(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction impossible.", { id: t });
    } finally { setReassigning(false); }
  };

  // Alignement STRICT immédiat sur Evergiving (miroir) : met à jour les statuts périmés
  // (ex. bulletins passés en « Invalide » / annulés après welcome call → cessent de
  // compter), crée les manquants, retire les fantômes, dédoublonne. Corrige MAINTENANT
  // sans attendre le cycle automatique. Balaie le mois courant + précédent par tranches.
  const [mirroring, setMirroring] = useState(false);
  const doForceMirror = async () => {
    setMirroring(true);
    const t = toast.loading("Alignement sur Evergiving en cours… (mise à jour des statuts, retrait des fantômes)");
    try {
      const { data } = await api.post("/bulletins/sync/reporting/mirror", {});
      const maj = data?.updated || 0, fant = data?.ghosts_flagged || 0, dup = data?.dups_flagged || 0;
      const total = maj + fant + dup;
      const skipped = data?.chunks_skipped || 0;
      if (data?.ok === false) {
        toast.error("Source Evergiving injoignable pour le moment — réessayez dans quelques minutes.", { id: t });
      } else {
        toast.success(
          (total > 0
            ? `Aligné sur Evergiving : ${maj.toLocaleString("fr-FR")} statut(s) corrigé(s), ${fant.toLocaleString("fr-FR")} fantôme(s) retiré(s), ${dup.toLocaleString("fr-FR")} doublon(s). Compteurs à jour.`
            : "Déjà parfaitement aligné sur Evergiving — rien à corriger.")
          + (skipped > 0 ? ` (${skipped} période(s) non vérifiée(s) : source lente — relancez dans 1 min.)` : ""),
          { id: t });
      }
      loadRepStatus(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alignement impossible.", { id: t });
    } finally { setMirroring(false); }
  };

  // [SECOURS] « Les bulletins ne remontent plus » : débloque le verrou de la synchro
  // automatique (verrou « zombie » qui stoppe tout) puis force une synchro + miroir du mois
  // courant. Affiche un diagnostic clair (verrou avant/après, compteurs, erreurs). C'est LE
  // bouton à utiliser en cas de blocage de la remontée automatique.
  const [unlocking, setUnlocking] = useState(false);
  const [repairReport, setRepairReport] = useState(null);
  const [repairOpen, setRepairOpen] = useState(false);
  const doForceUnlock = async () => {
    setUnlocking(true);
    const t = toast.loading("Déblocage de la synchronisation en cours…");
    try {
      const { data } = await api.post("/bulletins/sync/reporting/force-unlock", {});
      setRepairReport(data);
      setRepairOpen(true);
      const r = data?.resume || {};
      const cree = (r.sync_cree || 0) + (r.miroir_cree || 0);
      const maj = (r.sync_maj || 0) + (r.miroir_maj || 0);
      if (data?.ok === false) {
        toast.error(data?.error || "Déblocage effectué, mais la synchro a rencontré un souci — voir le détail.", { id: t });
      } else {
        toast.success(`Synchro débloquée et relancée : ${cree.toLocaleString("fr-FR")} créé(s), ${maj.toLocaleString("fr-FR")} mis à jour.`, { id: t });
      }
      loadRepStatus(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Déblocage impossible.", { id: t });
    } finally { setUnlocking(false); }
  };

  const applyPreset = (key) => {
    setPreset(key);
    const r = PERIODE_PRESETS[key].range();
    setFilters((f) => ({ ...f, dateFrom: r.from, dateTo: r.to }));
  };
  const setDate = (key, value) => {
    setPreset("custom");
    setFilters((f) => ({ ...f, [key]: value }));
  };
  const resetFilters = () => {
    const r = PERIODE_PRESETS[DEFAULT_PRESET].range();
    setPreset(DEFAULT_PRESET);
    setFilters({ spot: null, rd: null, source: "api", dateFrom: r.from, dateTo: r.to });
  };
  const isDefault = !filters.spot && !filters.rd && preset === DEFAULT_PRESET;

  const removeRow = async (id) => { await api.delete(`/bulletins/${id}`); toast.success("Bulletin supprimé"); load(); };

  // --- Audit & nettoyage des doublons ---------------------------------------------
  const openAudit = async () => {
    setAuditOpen(true); setAudit(null); setAuditLoading(true); setPurgeExcel(false);
    try {
      const { data } = await api.get("/bulletins/audit-doublons");
      setAudit(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit impossible.");
      setAuditOpen(false);
    } finally { setAuditLoading(false); }
  };
  const runClean = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/bulletins/nettoyer-doublons", { confirm: true, purge_excel: purgeExcel });
      toast.success(`Nettoyage terminé : ${(data.supprimes || 0).toLocaleString("fr-FR")} doublon(s) supprimé(s) · ${data.avant.toLocaleString("fr-FR")} → ${data.apres.toLocaleString("fr-FR")}.`);
      const { data: fresh } = await api.get("/bulletins/audit-doublons");
      setAudit(fresh);
      loadRepStatus(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nettoyage impossible.");
    } finally { setCleaning(false); }
  };

  return (
    <>
      <PageHeader title="Bulletins & RD" subtitle="Saisie et suivi des dons recrutés, par RD et par spot."
        action={<Button onClick={() => setShowNew(true)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="new-bulletin-btn"><Plus className="mr-2 h-4 w-4" /> Nouveau bulletin</Button>} />
      <PageBody>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--border-soft)] bg-white p-4" data-testid="reporting-sync-card">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Cloud className="h-5 w-5 text-[#3B5BDB]" /></div>
            <div>
              <div className="flex items-center gap-2 text-sm font-bold">
                Source automatique : Reporting
                {repStatus && (repStatus.configured
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#0F5A2F]" data-testid="reporting-configured-badge"><CheckCircle2 className="h-3 w-3" /> Connectée</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(185,137,0,0.14)] px-2 py-0.5 text-[10px] font-bold text-[#8a6600]" data-testid="reporting-notconfigured-badge"><AlertTriangle className="h-3 w-3" /> À configurer</span>)}
              </div>
              <div className="mt-0.5 text-xs text-muted-ink" data-testid="reporting-sync-status">
                {!repStatus ? "Chargement du statut…"
                  : !repStatus.configured
                    ? "Connexion à configurer (URL Reporting + jeton de service) — contactez l'administrateur technique."
                    : repStatus.last_run && repStatus.last_run.finished_at
                      ? (repStatus.last_run.ok === false
                          ? `Dernière tentative en échec (${repStatus.last_run.error || "erreur"}). Réessayez.`
                          : `Dernière synchro ${frenchDate(String(repStatus.last_run.finished_at).slice(0, 10))} · ${(repStatus.last_run.created || 0).toLocaleString("fr-FR")} créés, ${(repStatus.last_run.updated || 0).toLocaleString("fr-FR")} mis à jour${repStatus.last_run.unresolved_spot ? ` · ${repStatus.last_run.unresolved_spot} à rattacher` : ""} · ${(repStatus.total_bulletins_api || 0).toLocaleString("fr-FR")} bulletins via API`)
                      : `Aucune synchro encore exécutée · synchro automatique toutes les ${repStatus.interval_min} min.`}
              </div>
              {repStatus?.configured && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0F5A2F]" data-testid="reporting-auto-note">
                  <RefreshCw className="h-3 w-3" /> Synchronisation automatique en temps réel — les réattributions faites dans Evergiving sont reprises toutes seules (aucune action requise).
                </div>
              )}
              {repStatus?.last_run?.unresolved_lieux?.length > 0 && (
                <div className="mt-1 text-[11px] text-[#8a6600]" data-testid="reporting-unresolved-lieux">
                  Lieux à rattacher : {repStatus.last_run.unresolved_lieux.slice(0, 3).map((u) => u.label).join(" · ")}{repStatus.last_run.unresolved_lieux.length > 3 ? "…" : ""}
                </div>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {repStatus?.configured && repStatus?.last_run?.unresolved_spot ? (
              <button type="button" onClick={() => setLieuxOpen(true)} data-testid="reporting-lieux-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(185,137,0,0.4)] bg-[rgba(185,137,0,0.08)] px-2.5 py-1 text-[11px] font-bold text-[#8a6600] transition-colors hover:bg-[rgba(185,137,0,0.16)]">
                <MapPin className="h-3.5 w-3.5" /> {repStatus.last_run.unresolved_spot} lieu(x) à rattacher
              </button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="reporting-tools-menu-btn"
                  className="border-[var(--border-soft)] transition-transform duration-150 hover:-translate-y-px hover:bg-[var(--surface-2)]">
                  <MoreVertical className="mr-2 h-4 w-4" /> Outils de synchronisation
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Synchronisation Reporting</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {repStatus?.configured && (
                  <DropdownMenuItem onSelect={doForceUnlock} disabled={unlocking} data-testid="reporting-force-unlock-btn"
                    className="font-semibold text-[#B91C1C] focus:text-[#B91C1C] focus:bg-[rgba(185,28,28,0.08)]">
                    <LifeBuoy className="mr-2 h-4 w-4" /> Débloquer &amp; forcer la synchro
                  </DropdownMenuItem>
                )}
                {repStatus?.configured && <DropdownMenuSeparator />}
                {repStatus?.configured && (
                  <DropdownMenuItem onSelect={doForceMirror} disabled={mirroring} data-testid="reporting-mirror-btn">
                    <CheckCheck className="mr-2 h-4 w-4 text-[#0F5A2F]" /> Forcer l'alignement Evergiving (statuts + fantômes)
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => openPreview()} disabled={!repStatus?.configured} data-testid="reporting-sync-btn">
                  <RefreshCw className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" /> Forcer une synchro maintenant
                </DropdownMenuItem>
                {repStatus?.configured && (
                  <DropdownMenuItem onSelect={() => setReconcileOpen(true)} data-testid="reporting-reconcile-btn">
                    <ShieldCheck className="mr-2 h-4 w-4 text-[#0F5A2F]" /> Réconcilier (audit avancé)
                  </DropdownMenuItem>
                )}
                {repStatus?.configured && (
                  <DropdownMenuItem onSelect={() => setLieuxOpen(true)} data-testid="reporting-lieux-btn">
                    <MapPin className="mr-2 h-4 w-4 text-[#8a6600]" /> Lieux à rattacher
                    {repStatus?.last_run?.unresolved_spot ? <span className="ml-auto rounded-full bg-[rgba(185,137,0,0.18)] px-1.5 text-[11px] font-bold text-[#8a6600]">{repStatus.last_run.unresolved_spot}</span> : null}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => openAudit()} data-testid="reporting-audit-btn">
                  <Copy className="mr-2 h-4 w-4 text-muted-ink" /> Auditer les doublons
                </DropdownMenuItem>
                {repStatus?.configured && (
                  <DropdownMenuItem onSelect={() => setBackfillOpen(true)} data-testid="reporting-backfill-btn">
                    <History className="mr-2 h-4 w-4 text-muted-ink" /> Importer l'historique
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-3">
          <Filter className="h-4 w-4 text-muted-ink" />
          <EntitySearch endpoint="/spots" placeholder="Filtrer par spot…" value={filters.spot}
            onChange={(v) => setFilters((f) => ({ ...f, spot: v }))} labelFn={(s) => s.normalized_name} testId="filter-spot" icon={Store} />
          <EntitySearch endpoint="/rds" placeholder="Filtrer par RD…" value={filters.rd}
            onChange={(v) => setFilters((f) => ({ ...f, rd: v }))} labelFn={(r) => `${r.name}${r.code ? ` (${r.code})` : ""}`} testId="filter-rd" icon={User} />

          <div className="mx-1 hidden h-6 w-px bg-[var(--border-soft)] sm:block" />

          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="h-9 w-44" data-testid="filter-periode"><CalendarIcon className="mr-1 h-3.5 w-3.5 text-muted-ink" /><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PERIODE_PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
              <SelectItem value="custom" disabled className="text-muted-ink">Personnalisé</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            <Label htmlFor="bul-date-from" className="text-[11px] text-muted-ink">Du</Label>
            <Input id="bul-date-from" type="date" value={filters.dateFrom} max={filters.dateTo || undefined}
              onChange={(e) => setDate("dateFrom", e.target.value)} className="h-9 w-[150px]" data-testid="filter-date-from" />
            <Label htmlFor="bul-date-to" className="text-[11px] text-muted-ink">au</Label>
            <Input id="bul-date-to" type="date" value={filters.dateTo} min={filters.dateFrom || undefined}
              onChange={(e) => setDate("dateTo", e.target.value)} className="h-9 w-[150px]" data-testid="filter-date-to" />
          </div>

          {!isDefault && (
            <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="filter-clear">Réinitialiser</Button>
          )}
          <div className="ml-auto text-xs font-semibold text-muted-ink" data-testid="bulletins-total">{total.toLocaleString("fr-FR")} bulletin(s)</div>
        </div>

        <div className="rounded-2xl border border-[var(--border-soft)] bg-white">
          {rows === null ? (
            <div className="flex items-center justify-center py-16 text-muted-ink"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <ClipboardList className="h-8 w-8 text-muted-ink" />
              <p className="text-sm font-semibold">Aucun bulletin</p>
              <p className="text-xs text-muted-ink">Saisissez un bulletin ou ajustez les filtres.</p>
            </div>
          ) : (
            <Table data-testid="bulletins-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead><TableHead>Spot</TableHead><TableHead>RD</TableHead>
                  <TableHead className="text-right">Montant</TableHead><TableHead>Statut</TableHead>
                  <TableHead>Source</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((b) => (
                  <TableRow key={b.id} data-testid={`bulletin-row-${b.id}`}>
                    <TableCell className="text-xs font-medium">{frenchDate(b.date)}{b.horodatage ? ` · ${b.horodatage.slice(11, 16)}` : ""}</TableCell>
                    <TableCell className="max-w-[240px] text-xs">
                      {b.spot_name ? (
                        <span className="block break-words font-semibold" data-testid={`bulletin-spot-${b.id}`}>{b.spot_name}</span>
                      ) : (
                        <span className="block break-words text-muted-ink" title="Lieu non rattaché à un spot précis" data-testid={`bulletin-lieu-${b.id}`}>{b.ville || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {b.rd_id ? (
                        <button onClick={() => navigate(`/rds/${b.rd_id}`)} className="font-semibold text-[var(--shariti-orange)] hover:underline" data-testid={`bulletin-rd-link-${b.id}`}>{b.rd_name || b.recruiter_name || b.rd_code}</button>
                      ) : (
                        <div className="flex flex-col leading-tight" data-testid={`bulletin-rd-${b.id}`}>
                          <span className="font-medium">{b.recruiter_name || b.rd_name || "—"}</span>
                          {b.rd_code && <span className="text-[10px] text-muted-ink">{b.rd_code}</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs font-bold">{b.montant != null ? `${b.montant} €` : "—"}</TableCell>
                    <TableCell><span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATUT_TONE[b.statut] || "bg-[var(--surface-2)]"}`}>{b.statut || "—"}</span></TableCell>
                    <TableCell>
                      {b.source === "api" ? (
                        <Badge className="border-0 bg-[rgba(59,91,219,0.12)] text-[10px] text-[#3B5BDB]" data-testid={`bulletin-source-api-${b.id}`}><Cloud className="mr-1 h-2.5 w-2.5" />Reporting</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px]">{SOURCE_LABEL[b.source] || b.source}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {b.source === "manuel" && (
                        <button onClick={() => removeRow(b.id)} data-testid={`bulletin-del-${b.id}`} className="text-[#D64545] hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </PageBody>

      <NewBulletinDialog open={showNew} onOpenChange={setShowNew} onSaved={() => { setShowNew(false); load(); }} />
      <ReportingPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen}
        loading={previewLoading} preview={preview} onConfirm={confirmSync} confirming={confirming} />
      <UnresolvedLieuxDialog open={lieuxOpen} onOpenChange={setLieuxOpen}
        onChanged={() => { loadRepStatus(); load(); }} />
      <BackfillDialog open={backfillOpen} onOpenChange={setBackfillOpen}
        onDone={() => { loadRepStatus(); load(); }} />
      <ReconcileDialog open={reconcileOpen} onOpenChange={setReconcileOpen}
        onDone={() => { loadRepStatus(); load(); }} />

      {/* --- [SECOURS] Diagnostic du déblocage de la synchro --- */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="max-w-lg" data-testid="repair-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5 text-[#B91C1C]" /> Déblocage de la synchronisation
            </DialogTitle>
            <DialogDescription>
              Diagnostic de l'opération. En cas de souci persistant, transmettez cet écran au support.
            </DialogDescription>
          </DialogHeader>
          {repairReport && (
            <div className="space-y-3 text-sm" data-testid="repair-sync-report">
              <div className={`rounded-xl border px-3 py-2.5 ${repairReport.ok ? "border-[rgba(15,90,47,0.3)] bg-[rgba(31,157,85,0.08)]" : "border-[rgba(185,28,28,0.35)] bg-[rgba(185,28,28,0.06)]"}`}>
                <div className="flex items-center gap-2 font-semibold">
                  {repairReport.ok
                    ? <><CheckCircle2 className="h-4 w-4 text-[#0F5A2F]" /> Synchronisation débloquée et relancée</>
                    : <><AlertTriangle className="h-4 w-4 text-[#B91C1C]" /> Déblocage effectué — la synchro a rencontré un souci</>}
                </div>
                {repairReport.error && <div className="mt-1 text-[13px] text-[#B91C1C]" data-testid="repair-sync-error">{repairReport.error}</div>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wide text-muted-ink">Synchro incrémentale</div>
                  <div className="mt-0.5 font-semibold" data-testid="repair-sync-counts">
                    {(repairReport.resume?.sync_cree || 0).toLocaleString("fr-FR")} créé(s) · {(repairReport.resume?.sync_maj || 0).toLocaleString("fr-FR")} MAJ
                  </div>
                  <div className="text-[11px] text-muted-ink">{(repairReport.resume?.sync_recus || 0).toLocaleString("fr-FR")} reçus de Reporting</div>
                </div>
                <div className="rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wide text-muted-ink">Miroir mois courant</div>
                  <div className="mt-0.5 font-semibold" data-testid="repair-mirror-counts">
                    {(repairReport.resume?.miroir_cree || 0).toLocaleString("fr-FR")} créé(s) · {(repairReport.resume?.miroir_maj || 0).toLocaleString("fr-FR")} MAJ
                  </div>
                  <div className="text-[11px] text-muted-ink">{(repairReport.resume?.miroir_recus || 0).toLocaleString("fr-FR")} reçus de Reporting</div>
                </div>
              </div>
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">État du verrou</div>
                <div className="mt-0.5 text-[13px]" data-testid="repair-lock-state">
                  Avant : {repairReport.lock_before
                    ? <span className={repairReport.lock_before.running && !repairReport.lock_before.heartbeat ? "font-semibold text-[#B91C1C]" : ""}>
                        {repairReport.lock_before.running ? "occupé" : "libre"}{repairReport.lock_before.running && !repairReport.lock_before.heartbeat ? " (bloqué/zombie)" : ""}
                      </span>
                    : "aucun"} · Après : {repairReport.lock_after ? (repairReport.lock_after.running ? "occupé" : "libre") : "réinitialisé (sain)"}
                </div>
              </div>
              {Array.isArray(repairReport.steps) && repairReport.steps.length > 0 && (
                <ul className="list-inside list-disc space-y-0.5 text-[12px] text-muted-ink" data-testid="repair-steps">
                  {repairReport.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRepairOpen(false)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="repair-close-btn">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Audit & nettoyage des doublons --- */}
      <Dialog open={auditOpen} onOpenChange={(v) => { if (!cleaning) setAuditOpen(v); }}>
        <DialogContent className="max-w-2xl" data-testid="audit-doublons-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-[var(--shariti-orange)]" /> Audit des doublons de bulletins</DialogTitle>
            <DialogDescription>
              Analyse fiable fondée sur l'<b>ID pledge EverGiving</b> (id_externe). Aucune donnée n'est modifiée tant que vous ne lancez pas le nettoyage.
            </DialogDescription>
          </DialogHeader>

          {auditLoading || !audit ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-ink" data-testid="audit-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Analyse en cours…
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cartes de synthèse */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2.5" data-testid="audit-total">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-ink">Total actuel</div>
                  <div className="mt-0.5 text-xl font-bold tabular-nums text-ink">{audit.total.toLocaleString("fr-FR")}</div>
                </div>
                <div className="rounded-xl border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.06)] px-3 py-2.5" data-testid="audit-doublons-id">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[#9B2226]">Doublons (même ID)</div>
                  <div className="mt-0.5 text-xl font-bold tabular-nums text-[#9B2226]">{audit.doublons_id_externe.toLocaleString("fr-FR")}</div>
                </div>
                <div className="rounded-xl border border-[rgba(185,137,0,0.4)] bg-[rgba(185,137,0,0.06)] px-3 py-2.5" data-testid="audit-excel">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8a6600]">Import Excel EverGiving</div>
                  <div className="mt-0.5 text-xl font-bold tabular-nums text-[#8a6600]">{audit.import_excel_total.toLocaleString("fr-FR")}</div>
                </div>
              </div>

              {/* Projection après nettoyage */}
              <div className="rounded-xl border border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.06)] px-3 py-2.5 text-sm" data-testid="audit-projection">
                <div className="flex items-center gap-2 font-semibold text-[#0F5A2F]"><CheckCircle2 className="h-4 w-4" /> Après nettoyage</div>
                <div className="mt-1 text-[13px] text-ink">
                  Dédoublonnage seul : <b className="tabular-nums">{audit.total_apres_dedup.toLocaleString("fr-FR")}</b> bulletins.
                  {audit.import_excel_total > 0 && (
                    <> Avec purge de l'import Excel : <b className="tabular-nums">{audit.total_apres_dedup_et_purge_excel.toLocaleString("fr-FR")}</b>.</>
                  )}
                </div>
              </div>

              {audit.doublons_id_externe === 0 && audit.import_excel_total === 0 ? (
                <div className="flex items-center gap-2 rounded-xl border border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.06)] px-3 py-3 text-sm font-medium text-[#0F5A2F]" data-testid="audit-clean">
                  <CheckCircle2 className="h-4 w-4" /> Aucun doublon détecté : la base est saine.
                </div>
              ) : (
                <>
                  {/* Top recruteurs/mois impactés */}
                  {audit.top_recruteurs_mois?.length > 0 && (
                    <div>
                      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-ink">Recruteurs les plus impactés (mois)</div>
                      <div className="max-h-44 overflow-auto rounded-xl border border-[var(--border-soft)]">
                        <table className="w-full text-[13px]" data-testid="audit-top-table">
                          <thead className="sticky top-0 bg-[var(--surface-2)]">
                            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-ink">
                              <th className="px-3 py-1.5">Recruteur</th><th className="px-2 py-1.5">Mois</th>
                              <th className="px-2 py-1.5 text-right">Comptés</th><th className="px-2 py-1.5 text-right">Réels</th>
                              <th className="px-3 py-1.5 text-right">Doublons</th>
                            </tr>
                          </thead>
                          <tbody>
                            {audit.top_recruteurs_mois.map((r, i) => (
                              <tr key={i} className="border-t border-[var(--border-soft)]" data-testid={`audit-top-row-${i}`}>
                                <td className="px-3 py-1.5 font-medium text-ink">{r.rd_nom} <span className="text-muted-ink">({r.rd_code})</span></td>
                                <td className="px-2 py-1.5 tabular-nums text-muted-ink">{r.mois}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{r.comptes}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{r.reels}</td>
                                <td className="px-3 py-1.5 text-right font-bold tabular-nums text-[#9B2226]">+{r.doublons}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Option purge Excel */}
                  {audit.import_excel_total > 0 && (
                    <label className="flex items-start gap-2.5 rounded-xl border border-[rgba(185,137,0,0.4)] bg-[rgba(185,137,0,0.05)] px-3 py-2.5 text-[13px]" data-testid="audit-purge-toggle">
                      <Checkbox checked={purgeExcel} onCheckedChange={(v) => setPurgeExcel(!!v)} className="mt-0.5" data-testid="audit-purge-checkbox" />
                      <span className="text-ink">
                        Supprimer aussi les <b>{audit.import_excel_total.toLocaleString("fr-FR")}</b> bulletins de l'<b>import Excel EverGiving</b> (redondants avec la synchro API automatique).
                      </span>
                    </label>
                  )}

                  <div className="flex items-start gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[12px] text-muted-ink">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                    Le nettoyage conserve toujours la copie issue de la <b>synchro API</b> (source de vérité) et ne touche pas aux saisies manuelles. Action <b>irréversible</b>.
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)} disabled={cleaning} data-testid="audit-close-btn">Fermer</Button>
            {audit && (audit.doublons_id_externe > 0 || (purgeExcel && audit.import_excel_total > 0)) && (
              <Button onClick={runClean} disabled={cleaning}
                className="bg-[#9B2226] text-white hover:bg-[#7d1b1e] disabled:opacity-60" data-testid="audit-clean-btn">
                {cleaning ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Nettoyage…</> : <><Trash2 className="mr-2 h-4 w-4" /> Nettoyer maintenant</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Petit indicateur chiffré pour l'aperçu de synchro.
// Ligne explicite de l'aperçu de synchro : libellé + explication + valeur.
function PreviewLine({ label, hint, value, tone = "muted" }) {
  const tones = {
    green: "bg-[rgba(31,157,85,0.10)] text-[#0F5A2F]",
    blue: "bg-[rgba(59,91,219,0.10)] text-[#3B5BDB]",
    amber: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]",
    muted: "bg-[var(--surface-2)] text-ink",
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-soft)] px-3 py-2">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ink">{label}</div>
        <div className="text-[11px] text-muted-ink">{hint}</div>
      </div>
      <div className={`shrink-0 rounded-lg px-3 py-1 text-lg font-extrabold tabular-nums ${tones[tone] || tones.muted}`}>
        {(value || 0).toLocaleString("fr-FR")}
      </div>
    </div>
  );
}

function BackfillDialog({ open, onOpenChange, onDone }) {
  const [from, setFrom] = useState("2023-08-15");
  const [to, setTo] = useState("");
  const [launching, setLaunching] = useState(false);
  const [prog, setProg] = useState(null);
  const timer = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/bulletins/sync/reporting/status");
      setProg(data.backfill || null);
      if (data.backfill && !data.backfill.running && data.backfill.finished_at) {
        stop();
        doneRef.current?.();
      }
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    if (open) { poll(); } else { stop(); setProg(null); }
    return stop;
  }, [open, poll]);

  useEffect(() => {
    if (open && prog?.running && !timer.current) timer.current = setInterval(poll, 3000);
  }, [open, prog, poll]);

  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post("/bulletins/sync/reporting/backfill", { date_from: from, date_to: to || undefined });
      if (data.started === false && data.running) toast.message("Un import est déjà en cours.");
      if (!timer.current) timer.current = setInterval(poll, 3000);
      poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import de l'historique impossible.");
    } finally { setLaunching(false); }
  };

  const running = prog?.running;
  const pct = prog?.chunks_total ? Math.round((prog.chunks_done / prog.chunks_total) * 100) : 0;
  const justFinished = prog && !prog.running && prog.finished_at && prog.chunks_done > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="backfill-dialog">
        <DialogHeader>
          <DialogTitle>Importer l'historique complet</DialogTitle>
          <DialogDescription>Récupère tous les bulletins de Reporting sur la période choisie (par tranches, en arrière-plan). Vous pouvez fermer cette fenêtre : l'import continue.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Depuis</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={running} className="mt-1" data-testid="backfill-from" />
            </div>
            <div>
              <Label className="text-xs">Jusqu'à (optionnel)</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={running} className="mt-1" data-testid="backfill-to" />
            </div>
          </div>

          {prog && (prog.running || justFinished) && (
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="backfill-progress">
              <div className="mb-1 flex items-center justify-between text-xs font-semibold">
                <span>{running ? "Import en cours…" : "Import terminé"}</span>
                <span className="tabular-nums">{prog.chunks_done}/{prog.chunks_total || "?"} tranches</span>
              </div>
              <Progress value={running ? pct : 100} className="h-2" />
              <div className="mt-2 text-[11px] text-muted-ink">
                {(prog.created || 0).toLocaleString("fr-FR")} nouveaux · {(prog.unchanged || 0).toLocaleString("fr-FR")} déjà présents · {(prog.unresolved_spot || 0).toLocaleString("fr-FR")} à rattacher
              </div>
              {justFinished && <div className="mt-1 flex items-center gap-1 text-[11px] font-bold text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" /> Terminé.</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid="backfill-close">Fermer</Button>
          <Button onClick={launch} disabled={launching || running}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-60" data-testid="backfill-launch">
            {launching || running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Import…</> : <><History className="mr-2 h-4 w-4" /> Lancer l'import</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RÉCONCILIATION Booking ⇆ Reporting : neutralise les bulletins « fantômes » (présents
// dans Booking, absents du Reporting). Simulation d'abord, puis application.
function DiagList({ title, tone, rows, testid }) {
  return (
    <div className="rounded-lg bg-white p-2 text-[11px]" data-testid={testid}>
      <div className="mb-1 font-bold" style={{ color: tone }}>{title} · {rows.length}</div>
      <div className="max-h-32 overflow-y-auto">
        {rows.slice(0, 50).map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-2 border-b border-[var(--border-soft)] py-0.5 last:border-0">
            <span className="shrink-0 tabular-nums text-ink">{r.date}</span>
            <span className="min-w-0 flex-1 truncate text-muted-ink" title={r.lieu || ""}>{r.lieu || "—"}</span>
            <span className="shrink-0 text-muted-ink">{r.statut || "—"}</span>
            <span className="shrink-0 font-mono text-[10px] text-muted-ink">{r.id_externe || "sans id"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReconcileDialog({ open, onOpenChange, onDone }) {
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);
  const [askApply, setAskApply] = useState(false);
  // Diagnostic ciblé (un recruteur × un mois) — PREUVE de l'origine d'un écart
  const [diagCode, setDiagCode] = useState("");
  const [diagMois, setDiagMois] = useState(() => new Date().toISOString().slice(0, 7));
  const [diagBusy, setDiagBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  // Audit global des doublons (toute la base) — « d'autres personnes concernées ? »
  const [auditBusy, setAuditBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const timer = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/bulletins/sync/reporting/reconcile/status");
      setProg(data || null);
      if (data && !data.running && data.finished_at) { stop(); doneRef.current?.(); }
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    if (open) { poll(); } else { stop(); setProg(null); setAskApply(false); setDiag(null); setAudit(null); }
    return stop;
  }, [open, poll]);

  useEffect(() => {
    if (open && prog?.running && !timer.current) timer.current = setInterval(poll, 2500);
  }, [open, prog, poll]);

  const launch = async (dry_run) => {
    setBusy(true); setAskApply(false);
    try {
      const { data } = await api.post("/bulletins/sync/reporting/reconcile",
        { date_from: from, date_to: to || undefined, dry_run });
      if (data.started === false && data.running) toast.message("Une réconciliation est déjà en cours.");
      else toast.message(dry_run ? "Simulation lancée…" : "Réconciliation lancée…");
      if (!timer.current) timer.current = setInterval(poll, 2500);
      poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réconciliation impossible.");
    } finally { setBusy(false); }
  };

  const runDiag = async () => {
    const code = diagCode.trim().toUpperCase();
    if (!code) { toast.message("Saisissez un code recruteur (ex. AT0536)."); return; }
    if (!/^\d{4}-\d{2}$/.test(diagMois)) { toast.message("Choisissez un mois valide."); return; }
    setDiagBusy(true); setDiag(null);
    try {
      const { data } = await api.get("/bulletins/sync/reporting/diag", { params: { code, mois: diagMois } });
      if (data?.ok === false) toast.error(data.message || "Diagnostic impossible.");
      setDiag(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic impossible.");
    } finally { setDiagBusy(false); }
  };

  const runAudit = async () => {
    setAuditBusy(true); setAudit(null);
    try {
      const { data } = await api.get("/bulletins/sync/reporting/audit-duplicates");
      setAudit(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit impossible.");
    } finally { setAuditBusy(false); }
  };

  const running = prog?.running;
  const pct = prog?.days_total ? Math.round((prog.days_done / prog.days_total) * 100) : 0;
  const done = prog && !prog.running && prog.finished_at && (prog.days_done > 0 || prog.scanned > 0);
  const rep = prog?.last_report || prog;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) onOpenChange(v); }}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#0F5A2F]" /> Réconcilier avec Reporting</DialogTitle>
          <DialogDescription>
            Aligne Booking sur la <b>source de vérité (Reporting)</b> : les bulletins présents dans Booking mais <b>absents du Reporting</b> (« fantômes ») sont neutralisés et cessent de compter. C'est <b>réversible</b> (ils re-comptent s'ils réapparaissent). Lancez d'abord une <b>simulation</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Depuis</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={running} className="mt-1" data-testid="reconcile-from" />
            </div>
            <div>
              <Label className="text-xs">Jusqu'à (optionnel)</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={running} className="mt-1" data-testid="reconcile-to" />
            </div>
          </div>

          {/* Diagnostic ciblé — PREUVE de l'origine d'un écart (ex. « 36 vs 35 ») */}
          <div className="rounded-xl border border-[rgba(31,157,85,0.25)] bg-[rgba(31,157,85,0.04)] p-3" data-testid="reconcile-diag">
            <div className="flex items-center gap-1.5 text-[12px] font-bold text-[#0F5A2F]">
              <Stethoscope className="h-4 w-4" /> Diagnostic d'un recruteur
            </div>
            <p className="mt-0.5 text-[11px] text-muted-ink">
              Comparez, pour un recruteur et un mois, le décompte <b>Booking</b> au décompte <b>Reporting</b> (temps réel). Explique l'origine exacte d'un écart : doublon interne, fantôme, ou statut exclu.
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <div className="min-w-[120px] flex-1">
                <Label className="text-[10px]">Code recruteur</Label>
                <Input value={diagCode} onChange={(e) => setDiagCode(e.target.value)} placeholder="AT0536" className="mt-1 h-9" data-testid="diag-code" />
              </div>
              <div className="min-w-[130px]">
                <Label className="text-[10px]">Mois</Label>
                <Input type="month" value={diagMois} onChange={(e) => setDiagMois(e.target.value)} className="mt-1 h-9" data-testid="diag-mois" />
              </div>
              <Button size="sm" onClick={runDiag} disabled={diagBusy}
                className="h-9 bg-[#0F5A2F] text-white hover:bg-[#0c4a26]" data-testid="diag-run">
                {diagBusy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> …</> : <><Search className="mr-1.5 h-4 w-4" /> Diagnostiquer</>}
              </Button>
            </div>

            {diag && diag.ok !== false && (
              <div className="mt-2 space-y-2" data-testid="diag-result">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">Booking</div><div className="text-sm font-bold tabular-nums text-ink" data-testid="diag-booking">{diag.booking_count}</div></div>
                  <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">Reporting</div><div className="text-sm font-bold tabular-nums text-ink" data-testid="diag-reporting">{diag.reporting_count}</div></div>
                  <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">Écart</div><div className={`text-sm font-bold tabular-nums ${diag.ecart === 0 ? "text-[#0F5A2F]" : "text-[#9B2226]"}`} data-testid="diag-ecart">{diag.ecart > 0 ? `+${diag.ecart}` : diag.ecart}</div></div>
                </div>
                <div className={`rounded-lg p-2 text-[11px] font-semibold ${diag.ecart === 0 ? "bg-[rgba(31,157,85,0.1)] text-[#0F5A2F]" : "bg-[rgba(214,69,69,0.08)] text-[#9B2226]"}`} data-testid="diag-verdict">{diag.verdict}</div>
                {diag.internal_dups?.length > 0 && <DiagList title="Doublons internes (à neutraliser)" tone="#8a6600" rows={diag.internal_dups} testid="diag-dups" />}
                {diag.ghosts?.length > 0 && <DiagList title="Fantômes (absents du Reporting)" tone="#9B2226" rows={diag.ghosts} testid="diag-ghosts" />}
                {diag.reporting_only?.length > 0 && <DiagList title="Dans Reporting, pas encore dans Booking" tone="#0F5A2F" rows={diag.reporting_only} testid="diag-reponly" />}
                {diag.fixable_here > 0 && (
                  <p className="text-[11px] text-muted-ink">Lancez <b>Simuler</b> puis <b>Appliquer</b> ci-dessous pour corriger automatiquement ({diag.fixable_here} bulletin(s)).</p>
                )}
              </div>
            )}
          </div>

          {/* Audit GLOBAL des doublons — « d'autres personnes concernées ? » */}
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="reconcile-audit">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[12px] font-bold text-ink">
                <ClipboardList className="h-4 w-4 text-[#8a6600]" /> Audit des doublons (toute la base)
              </div>
              <Button size="sm" variant="outline" onClick={runAudit} disabled={auditBusy} className="h-8" data-testid="audit-run">
                {auditBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Analyse…</> : <><Search className="mr-1.5 h-3.5 w-3.5" /> Auditer</>}
              </Button>
            </div>
            <p className="mt-0.5 text-[11px] text-muted-ink">Vérifie si d'autres recruteurs sont/étaient concernés par des doublons internes, sur l'ensemble de la base.</p>

            {audit && audit.ok !== false && (
              <div className="mt-2 space-y-2" data-testid="audit-result">
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className={`rounded-lg p-2 ${(audit.remaining_dups || 0) === 0 ? "bg-[rgba(31,157,85,0.1)]" : "bg-[rgba(214,69,69,0.08)]"}`}>
                    <div className="text-[10px] uppercase text-muted-ink">Doublons restants</div>
                    <div className={`text-sm font-bold tabular-nums ${(audit.remaining_dups || 0) === 0 ? "text-[#0F5A2F]" : "text-[#9B2226]"}`} data-testid="audit-remaining">{(audit.remaining_dups || 0).toLocaleString("fr-FR")}</div>
                  </div>
                  <div className="rounded-lg bg-white p-2">
                    <div className="text-[10px] uppercase text-muted-ink">Déjà neutralisés</div>
                    <div className="text-sm font-bold tabular-nums text-[#8a6600]" data-testid="audit-cleaned">{(audit.cleaned_total || 0).toLocaleString("fr-FR")} chez {(audit.cleaned_recruteurs || 0)} recruteur(s)</div>
                  </div>
                </div>
                {(audit.remaining_dups || 0) === 0 ? (
                  <div className="flex items-center gap-1 rounded-lg bg-[rgba(31,157,85,0.1)] p-2 text-[11px] font-bold text-[#0F5A2F]"><CheckCircle2 className="h-3.5 w-3.5" /> Aucun doublon restant : toute la base est alignée sur Reporting.</div>
                ) : (
                  <div className="rounded-lg bg-[rgba(214,69,69,0.08)] p-2 text-[11px] font-semibold text-[#9B2226]">{(audit.remaining_dups || 0)} doublon(s) encore à neutraliser — lancez « Simuler » puis « Appliquer » ci-dessous.</div>
                )}
                {audit.remaining_by_recruteur?.length > 0 && (
                  <div className="rounded-lg bg-white p-2 text-[11px]" data-testid="audit-remaining-list">
                    <div className="mb-1 font-bold text-[#9B2226]">À corriger · {audit.remaining_by_recruteur.length} recruteur(s)</div>
                    {audit.remaining_by_recruteur.slice(0, 50).map((r, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-[var(--border-soft)] py-0.5 last:border-0"><span className="font-mono text-ink">{r.code}</span><span className="tabular-nums text-muted-ink">{r.dups} doublon(s)</span></div>
                    ))}
                  </div>
                )}
                {audit.cleaned?.length > 0 && (
                  <div className="rounded-lg bg-white p-2 text-[11px]" data-testid="audit-cleaned-list">
                    <div className="mb-1 font-bold text-[#8a6600]">Historique des doublons neutralisés</div>
                    <div className="max-h-40 overflow-y-auto">
                      {audit.cleaned.slice(0, 200).map((r, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 border-b border-[var(--border-soft)] py-0.5 last:border-0">
                          <span className="min-w-0 flex-1 truncate text-ink" title={r.nom || ""}>{r.nom || "—"}</span>
                          <span className="shrink-0 font-mono text-muted-ink">{r.code || "?"}</span>
                          <span className="shrink-0 tabular-nums text-muted-ink">{r.mois}</span>
                          <span className="shrink-0 font-bold tabular-nums text-[#8a6600]">{r.dups}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(audit.cleaned_total || 0) === 0 && (audit.remaining_dups || 0) === 0 && (
                  <p className="text-[11px] text-muted-ink">Aucun doublon interne n'a jamais été détecté dans cette base.</p>
                )}
              </div>
            )}
          </div>
          {prog && (running || done) && (
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="reconcile-progress">
              <div className="mb-1 flex items-center justify-between text-xs font-semibold">
                <span>{running ? (prog.dry_run ? "Simulation en cours…" : "Réconciliation en cours…") : (prog.dry_run ? "Simulation terminée" : "Réconciliation terminée")}</span>
                <span className="tabular-nums">{prog.days_done}/{prog.days_total || "?"} j</span>
              </div>
              <Progress value={running ? pct : 100} className="h-2" />
              <div className="mt-2 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
                <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">Scannés</div><div className="text-sm font-bold tabular-nums text-ink" data-testid="reconcile-scanned">{(prog.scanned || 0).toLocaleString("fr-FR")}</div></div>
                <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">Fantômes</div><div className="text-sm font-bold tabular-nums text-[#9B2226]" data-testid="reconcile-ghosts">{(prog.ghosts || 0).toLocaleString("fr-FR")}</div></div>
                <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">Doublons</div><div className="text-sm font-bold tabular-nums text-[#8a6600]" data-testid="reconcile-dups">{(prog.dups || 0).toLocaleString("fr-FR")}</div></div>
                <div className="rounded-lg bg-white p-2"><div className="text-[10px] uppercase text-muted-ink">{prog.dry_run ? "À neutraliser" : "Neutralisés"}</div><div className="text-sm font-bold tabular-nums text-[#0F5A2F]" data-testid="reconcile-flagged">{(prog.dry_run ? ((prog.ghosts || 0) + (prog.dups || 0)) : ((prog.flagged || 0) + (prog.dups_flagged || 0))).toLocaleString("fr-FR")}</div></div>
              </div>
              {(rep?.by_recruteur?.length > 0) && (
                <div className="mt-2 max-h-32 overflow-y-auto rounded-lg bg-white p-2 text-[11px]" data-testid="reconcile-by-recruteur">
                  <div className="mb-1 font-bold text-ink">Par recruteur :</div>
                  {rep.by_recruteur.slice(0, 30).map((r) => (
                    <div key={r.code} className="flex items-center justify-between border-b border-[var(--border-soft)] py-0.5 last:border-0">
                      <span className="font-semibold text-ink">{r.code}</span>
                      <span className="tabular-nums text-[#9B2226]">{r.ghosts} fantôme{r.ghosts > 1 ? "s" : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              {(rep?.skipped?.length > 0) && (
                <div className="mt-1 text-[10px] text-[#8a6600]">{rep.skipped.length} plage(s) ignorée(s) par sécurité (source saturée).</div>
              )}
              {done && prog.dry_run && (prog.ghosts || 0) === 0 && (prog.dups || 0) === 0 && (
                <div className="mt-1 flex items-center gap-1 text-[11px] font-bold text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" /> Aucun écart — Booking est déjà aligné sur Reporting.</div>
              )}
              {done && prog.dry_run && (prog.dups || 0) > 0 && (
                <div className="mt-1 text-[11px] font-semibold text-[#8a6600]">{(prog.dups || 0).toLocaleString("fr-FR")} doublon(s) interne(s) détecté(s) — « Appliquer » les neutralisera.</div>
              )}
              {done && !prog.dry_run && (
                <div className="mt-1 flex items-center gap-1 text-[11px] font-bold text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" /> {((prog.flagged || 0) + (prog.dups_flagged || 0)).toLocaleString("fr-FR")} bulletin(s) neutralisé(s) ({(prog.flagged || 0).toLocaleString("fr-FR")} fantôme(s), {(prog.dups_flagged || 0).toLocaleString("fr-FR")} doublon(s)).</div>
              )}
            </div>
          )}

          {askApply && (
            <div className="rounded-xl border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.06)] p-3 text-[12px]" data-testid="reconcile-confirm">
              <div className="font-bold text-[#9B2226]">Confirmer la neutralisation ?</div>
              <p className="mt-0.5 text-muted-ink">Les bulletins fantômes de la période cesseront de compter (réversible). Lancez de préférence une simulation avant.</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setAskApply(false)} data-testid="reconcile-cancel-apply">Annuler</Button>
                <Button size="sm" onClick={() => launch(false)} disabled={running}
                  className="bg-[#9B2226] text-white hover:bg-[#7d1b1e]" data-testid="reconcile-confirm-apply">Oui, neutraliser</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running} data-testid="reconcile-close">Fermer</Button>
          <Button variant="outline" onClick={() => launch(true)} disabled={busy || running}
            className="border-[var(--border-soft)]" data-testid="reconcile-simulate">
            {(busy || running) && prog?.dry_run ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Simulation…</> : <><Route className="mr-2 h-4 w-4" /> Simuler</>}
          </Button>
          <Button onClick={() => setAskApply(true)} disabled={busy || running}
            className="bg-[#0F5A2F] text-white hover:bg-[#0c4a26] disabled:opacity-60" data-testid="reconcile-apply">
            <ShieldCheck className="mr-2 h-4 w-4" /> Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ReportingPreviewDialog({ open, onOpenChange, loading, preview, onConfirm, confirming }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reporting-preview-dialog">
        <DialogHeader>
          <DialogTitle>Aperçu de la synchronisation Reporting</DialogTitle>
          <DialogDescription>Rien n'est encore écrit en base. Vérifiez le récapitulatif, puis confirmez.</DialogDescription>
        </DialogHeader>
        {loading || !preview ? (
          <div className="flex items-center justify-center py-10 text-muted-ink" data-testid="reporting-preview-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : preview.ok === false ? (
          <div className="rounded-xl bg-[rgba(214,69,69,0.08)] p-4 text-sm text-[#9B2226]" data-testid="reporting-preview-error">Aperçu impossible : {preview.message || preview.error}</div>
        ) : (
          <div className="space-y-2" data-testid="reporting-preview-body">
            <p className="text-xs text-muted-ink">
              Sur la période, Reporting a renvoyé <b className="text-ink">{(preview.fetched || 0).toLocaleString("fr-FR")}</b> bulletin(s). Voici ce que l'import va faire :
            </p>
            <PreviewLine tone="green" value={preview.created} label="Nouveaux bulletins à ajouter"
              hint="Dons pas encore enregistrés dans Booking" />
            <PreviewLine tone="blue" value={preview.updated} label="Bulletins à mettre à jour"
              hint="Déjà présents, mais une info a changé (statut, montant…)" />
            {(preview.rd_reassigned || 0) > 0 && (
              <PreviewLine tone="blue" value={preview.rd_reassigned} label="Attributions de recruteur à corriger"
                hint="Bulletins réattribués dans Evergiving (ex. tablette d'un collègue) — recomptés sur le bon collaborateur" />
            )}
            <PreviewLine tone="muted" value={preview.unchanged} label="Déjà à jour — rien à faire"
              hint="Identiques à ce qui est déjà dans Booking (aucun doublon créé)" />
            {(preview.rds_created || 0) > 0 && (
              <PreviewLine tone="muted" value={preview.rds_created} label="Nouveaux recruteurs (RD) créés"
                hint="Codes recruteurs inconnus ajoutés automatiquement" />
            )}
            <PreviewLine tone={preview.unresolved_spot ? "amber" : "muted"} value={preview.unresolved_spot} label="Lieux à rattacher"
              hint="Lieux en texte libre non reliés à un spot précis (à traiter via « Lieux à rattacher »)" />
            {preview.unresolved_lieux?.length > 0 && (
              <div className="mt-1 rounded-xl border border-[rgba(185,137,0,0.3)] bg-[rgba(185,137,0,0.06)] p-3 text-xs" data-testid="reporting-preview-unresolved">
                <div className="mb-1 font-bold text-[#8a6600]">Exemples de lieux non reconnus :</div>
                <ul className="list-disc pl-4 text-[#8a6600]">
                  {preview.unresolved_lieux.slice(0, 5).map((u, i) => (
                    <li key={i} className="break-words">{u.label} <span className="opacity-70">×{u.count}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reporting-preview-cancel">Annuler</Button>
          <Button onClick={onConfirm} disabled={loading || confirming || !preview || preview.ok === false}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-60" data-testid="reporting-preview-confirm">
            {confirming ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Import…</> : "Confirmer et importer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnresolvedLieuxDialog({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [kindFilter, setKindFilter] = useState("magasin"); // "magasin" | "terrain"
  const [attaching, setAttaching] = useState(null);
  const [sel, setSel] = useState({});      // lieu_key -> spot choisi manuellement
  const [checked, setChecked] = useState({}); // lieu_key -> true (cases à cocher)
  const [minScore, setMinScore] = useState("0.55");
  const [bulkPreview, setBulkPreview] = useState(null);  // {lieux, bulletins}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selBusy, setSelBusy] = useState(false);
  const [auto70Busy, setAuto70Busy] = useState(false);
  const [multiBusy, setMultiBusy] = useState(false);
  // Onglet « Rattachés » : correspondances existantes + annulation (undo).
  const [corr, setCorr] = useState(null);
  const [detaching, setDetaching] = useState(null);

  const load = useCallback((kind) => {
    setData(null); setBulkPreview(null);
    api.get(`/bulletins/unresolved-lieux?kind=${kind}`)
      .then((r) => setData(r.data)).catch(() => setData({ lieux: [], totaux: {} }));
  }, []);
  const loadCorr = useCallback(() => {
    setCorr(null);
    api.get("/bulletins/correspondances")
      .then((r) => setCorr(r.data)).catch(() => setCorr({ items: [], total: 0 }));
  }, []);
  useEffect(() => {
    if (!open) return;
    setSel({}); setChecked({});
    if (kindFilter === "attached") loadCorr();
    else load(kindFilter);
  }, [open, kindFilter, load, loadCorr]);

  // Aperçu du rattachement en masse « par seuil » (magasins uniquement).
  const previewBulk = useCallback(async (score) => {
    setBulkPreview(null);
    try {
      const { data: r } = await api.post("/bulletins/attach-lieu/bulk", { min_score: parseFloat(score), dry_run: true });
      setBulkPreview(r);
    } catch { /* silencieux */ }
  }, []);
  useEffect(() => {
    if (open && kindFilter === "magasin" && data && (data.total_lieux || 0) > 0) previewBulk(minScore);
  }, [open, kindFilter, data, minScore, previewBulk]);

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/bulletins/attach-lieu/bulk", { min_score: parseFloat(minScore), dry_run: false });
      toast.success(`${r.lieux} lieu(x) · ${r.bulletins.toLocaleString("fr-FR")} bulletin(s) rattaché(s) automatiquement.`);
      load(kindFilter); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rattachement en masse impossible.");
    } finally { setBulkBusy(false); }
  };

  // Auto-rattachement « en un clic » des magasins TRÈS SÛRS (≥70%). Le reste (incertain)
  // reste à valider à la main (sélection / rattachement individuel ci-dessous).
  const runAuto70 = async () => {
    setAuto70Busy(true);
    try {
      const { data: r } = await api.post("/bulletins/attach-lieu/bulk", { min_score: 0.70, dry_run: false });
      if ((r.lieux || 0) === 0) {
        toast.info("Aucun magasin très sûr (≥ 70 %) à rattacher automatiquement.");
      } else {
        toast.success(`${r.lieux} magasin(s) très sûrs · ${(r.bulletins || 0).toLocaleString("fr-FR")} bulletin(s) rattachés automatiquement. Il vous reste à valider les cas incertains à la main.`);
      }
      load(kindFilter); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rattachement automatique impossible.");
    } finally { setAuto70Busy(false); }
  };

  // Rattachement des libellés MULTI-MAGASINS via le planning du recruteur (prévu/pointé ce
  // jour-là). Chaque bulletin est rattaché au magasin du planning s'il figure dans le libellé.
  // S'exécute aussi AUTOMATIQUEMENT à chaque synchro ; ce bouton le lance tout de suite.
  const runMultistore = async () => {
    setMultiBusy(true);
    try {
      const { data: r } = await api.post("/bulletins/attach-lieu/multistore", { dry_run: false });
      if ((r.attached || 0) === 0) {
        toast.info("Aucun bulletin multi-magasins rattachable via le planning pour le moment.");
      } else {
        toast.success(`${(r.attached || 0).toLocaleString("fr-FR")} bulletin(s) multi-magasins rattaché(s) via le planning du recruteur.`);
      }
      load(kindFilter); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rattachement multi-magasins impossible.");
    } finally { setMultiBusy(false); }
  };

  // Annule (undo) un rattachement de lieu erroné : les bulletins redeviennent « à rattacher ».
  const detach = async (item) => {
    setDetaching(item.lieu_key);
    try {
      const { data: r } = await api.post("/bulletins/detach-lieu", { lieu_key: item.lieu_key });
      toast.success(`Rattachement annulé — ${r.detached} bulletin(s) « ${r.label} » redeviennent à rattacher.`);
      loadCorr(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Annulation impossible.");
    } finally { setDetaching(null); }
  };

  const attach = async (lieu) => {
    const spot = sel[lieu.lieu_key]
      || (lieu.suggested_spot_id ? { id: lieu.suggested_spot_id, normalized_name: lieu.suggested_name } : null);
    if (!spot) { toast.error("Choisissez un spot pour ce lieu."); return; }
    setAttaching(lieu.lieu_key);
    try {
      const { data: res } = await api.post("/bulletins/attach-lieu", { lieu_key: lieu.lieu_key, spot_id: spot.id });
      toast.success(`${res.attached} bulletin(s) rattaché(s) à ${res.spot_name}.`);
      load(kindFilter);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rattachement impossible.");
    } finally { setAttaching(null); }
  };

  const lieux = data?.lieux || [];
  const tot = data?.totaux || { magasin: { lieux: 0, bulletins: 0 }, terrain: { lieux: 0, bulletins: 0 } };
  // Une ligne est « rattachable » si elle a une suggestion OU un spot choisi à la main.
  const hasTarget = (l) => !!(sel[l.lieu_key] || l.suggested_spot_id);
  const eligible = lieux.filter(hasTarget);
  const selectedKeys = lieux.filter((l) => checked[l.lieu_key] && hasTarget(l));
  const selBulletins = selectedKeys.reduce((s, l) => s + (l.count || 0), 0);

  const toggleAll = () => {
    if (eligible.every((l) => checked[l.lieu_key])) {
      setChecked({});
    } else {
      const next = {};
      eligible.forEach((l) => { next[l.lieu_key] = true; });
      setChecked(next);
    }
  };
  const selectByScore = (thr) => {
    const next = { ...checked };
    lieux.forEach((l) => { if (hasTarget(l) && (l.score || 0) >= thr) next[l.lieu_key] = true; });
    setChecked(next);
  };

  const attachSelection = async () => {
    if (selectedKeys.length === 0) return;
    setSelBusy(true);
    try {
      const items = selectedKeys.map((l) => ({
        lieu_key: l.lieu_key,
        spot_id: sel[l.lieu_key]?.id || undefined, // sinon la suggestion côté serveur
      }));
      const { data: r } = await api.post("/bulletins/attach-lieu/selection", { items, dry_run: false });
      toast.success(`${r.lieux} lieu(x) · ${(r.bulletins || 0).toLocaleString("fr-FR")} bulletin(s) rattaché(s).`);
      setChecked({});
      load(kindFilter); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rattachement de la sélection impossible.");
    } finally { setSelBusy(false); }
  };

  const KindTab = ({ value, icon: Icon, label, count }) => (
    <button
      onClick={() => setKindFilter(value)}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${kindFilter === value ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}
      data-testid={`unresolved-tab-${value}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
      <span className={`ml-0.5 rounded-full px-1.5 text-[10px] ${kindFilter === value ? "bg-white/25" : "bg-[var(--surface-2)]"}`}>{count}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="unresolved-lieux-dialog">
        <DialogHeader>
          <DialogTitle>Lieux à rattacher</DialogTitle>
          <DialogDescription>
            Les lieux <b>Magasin</b> (enseigne reconnue) se rattachent à un spot. Les lieux <b>Terrain</b>
            {" "}(ville seule / rue / itinérant) ne sont <b>pas</b> reliés à un magasin. Le rattachement est mémorisé : les prochaines synchros le résolvent seules.
          </DialogDescription>
        </DialogHeader>

        {/* Onglets Magasin / Terrain */}
        <div className="flex items-center gap-1.5 rounded-xl border border-[var(--border-soft)] bg-white p-1" data-testid="unresolved-tabs">
          <KindTab value="magasin" icon={Store} label="Magasins" count={(tot.magasin?.lieux || 0).toLocaleString("fr-FR")} />
          <KindTab value="terrain" icon={Route} label="Terrain (rue/PAP)" count={(tot.terrain?.lieux || 0).toLocaleString("fr-FR")} />
          <KindTab value="attached" icon={Link2} label="Rattachés" count={corr ? (corr.total || 0).toLocaleString("fr-FR") : "…"} />
        </div>

        {/* Résumé du type courant */}
        {data && kindFilter !== "attached" && (
          <div className="text-[11px] text-muted-ink" data-testid="unresolved-summary">
            {kindFilter === "magasin"
              ? <><b className="text-ink">{(tot.magasin?.lieux || 0).toLocaleString("fr-FR")}</b> magasin(s) · <b className="text-ink">{(tot.magasin?.bulletins || 0).toLocaleString("fr-FR")}</b> bulletin(s) à rattacher</>
              : <><b className="text-ink">{(tot.terrain?.lieux || 0).toLocaleString("fr-FR")}</b> lieu(x) terrain · <b className="text-ink">{(tot.terrain?.bulletins || 0).toLocaleString("fr-FR")}</b> bulletin(s) (rue / porte-à-porte / itinérant)</>}
            {data.shown < (kindFilter === "magasin" ? tot.magasin?.lieux : tot.terrain?.lieux)
              ? <span> · {data.shown} affichés (les plus fréquents)</span> : null}
          </div>
        )}

        {/* Barre d'actions MAGASIN : sélection par cases à cocher + rattachement en masse par seuil */}
        {kindFilter === "magasin" && data && lieux.length > 0 && (
          <div className="space-y-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="unresolved-actions">
            <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border-soft)] pb-2">
              <Button size="sm" onClick={runAuto70} disabled={auto70Busy}
                className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-60" data-testid="auto70-btn">
                {auto70Busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                Auto-rattacher les magasins très sûrs (≥ 70 %)
              </Button>
              <Button size="sm" variant="outline" onClick={runMultistore} disabled={multiBusy}
                className="h-8 border-[var(--border-soft)] disabled:opacity-60" data-testid="multistore-btn"
                title="Pour les lieux listant plusieurs magasins : rattache chaque bulletin au magasin où le recruteur était prévu ce jour-là. Se fait aussi automatiquement à chaque synchro.">
                {multiBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Route className="mr-1.5 h-3.5 w-3.5 text-[#0F5A2F]" />}
                Rattacher les multi-magasins (via planning)
              </Button>
              <span className="text-[11px] text-muted-ink">En un clic. Les cas incertains restent à valider à la main ci-dessous.</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" className="h-8" onClick={toggleAll} data-testid="sel-toggle-all">
                <CheckCheck className="mr-1.5 h-3.5 w-3.5" />
                {eligible.length > 0 && eligible.every((l) => checked[l.lieu_key]) ? "Tout décocher" : "Tout cocher (éligibles)"}
              </Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => selectByScore(0.7)} data-testid="sel-score-70">Cocher ≥ 70%</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => selectByScore(0.55)} data-testid="sel-score-55">Cocher ≥ 55%</Button>
              {selectedKeys.length > 0 && (
                <Button size="sm" variant="ghost" className="h-8 text-muted-ink" onClick={() => setChecked({})} data-testid="sel-clear"><X className="mr-1 h-3.5 w-3.5" />Effacer</Button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[11px] font-semibold text-ink" data-testid="sel-count">
                  {selectedKeys.length} sélectionné(s) · {selBulletins.toLocaleString("fr-FR")} bulletin(s)
                </span>
                <Button size="sm" onClick={attachSelection} disabled={selBusy || selectedKeys.length === 0}
                  className="h-8 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-60" data-testid="sel-attach-btn">
                  {selBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Rattachement…</> : <>Rattacher la sélection</>}
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border-soft)] pt-2">
              <span className="text-[11px] font-semibold text-muted-ink">Ou en masse selon les suggestions, seuil :</span>
              <Select value={minScore} onValueChange={setMinScore}>
                <SelectTrigger className="h-8 w-[170px]" data-testid="bulk-threshold"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.7">Très sûr (≥ 70%)</SelectItem>
                  <SelectItem value="0.55">Assez sûr (≥ 55%)</SelectItem>
                  <SelectItem value="0.4">Large (≥ 40%)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={runBulk} disabled={bulkBusy || !bulkPreview || (bulkPreview.lieux || 0) === 0}
                variant="outline" className="h-8" data-testid="bulk-attach-btn">
                {bulkBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> …</>
                  : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Rattacher {bulkPreview ? `${bulkPreview.lieux} lieu(x)` : "…"}</>}
              </Button>
              {bulkPreview && (
                <span className="text-[11px] text-muted-ink" data-testid="bulk-preview">
                  {(bulkPreview.lieux || 0) > 0
                    ? `→ ${bulkPreview.lieux} lieu(x) · ${(bulkPreview.bulletins || 0).toLocaleString("fr-FR")} bulletin(s)`
                    : "→ Aucune suggestion à ce seuil."}
                </span>
              )}
            </div>
          </div>
        )}

        {kindFilter === "attached" ? (
          corr === null ? (
            <div className="flex items-center justify-center py-10 text-muted-ink"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (corr.items || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center" data-testid="corr-empty">
              <Link2 className="h-8 w-8 text-muted-ink" />
              <p className="text-sm font-semibold">Aucun lieu rattaché</p>
              <p className="text-xs text-muted-ink">Rattachez des magasins depuis l'onglet « Magasins ».</p>
            </div>
          ) : (
            <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1" data-testid="corr-list">
              <div className="flex items-start gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-[12px] text-muted-ink">
                <History className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Ces lieux Reporting sont rattachés à un magasin. En cas d'erreur de saisie, annulez le rattachement : les bulletins concernés redeviennent « à rattacher ».</span>
              </div>
              {(corr.items || []).map((it) => (
                <div key={it.lieu_key} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-soft)] px-3 py-2" data-testid={`corr-row-${it.lieu_key}`}>
                  <div className="min-w-0">
                    <div className="break-words text-sm font-semibold">{it.label}</div>
                    <div className="text-[11px] text-muted-ink">
                      <Store className="mr-1 inline h-3 w-3" />{it.spot_name}{it.ville ? ` · ${it.ville}` : ""} · {(it.count || 0).toLocaleString("fr-FR")} bulletin(s)
                    </div>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" disabled={detaching === it.lieu_key}
                        className="h-8 shrink-0 border-[rgba(214,69,69,0.4)] text-[#B23434] hover:bg-[rgba(214,69,69,0.08)] hover:text-[#B23434] disabled:opacity-60"
                        data-testid={`corr-detach-${it.lieu_key}`}>
                        {detaching === it.lieu_key ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}Annuler
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-64" data-testid={`corr-confirm-${it.lieu_key}`}>
                      <p className="text-sm font-semibold text-ink">Annuler ce rattachement ?</p>
                      <p className="mt-1 text-[12px] text-muted-ink">Les bulletins « {it.label} » redeviendront « à rattacher ».</p>
                      <div className="mt-3 flex justify-end">
                        <Button size="sm" onClick={() => detach(it)} disabled={detaching === it.lieu_key}
                          className="h-8 bg-[#D64545] text-white hover:bg-[#b93a3a]" data-testid={`corr-confirm-btn-${it.lieu_key}`}>
                          Confirmer l'annulation
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              ))}
            </div>
          )
        ) : data === null ? (
          <div className="flex items-center justify-center py-10 text-muted-ink"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : lieux.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center" data-testid="unresolved-empty">
            <CheckCircle2 className="h-8 w-8 text-[#1F9D55]" />
            <p className="text-sm font-semibold">{kindFilter === "magasin" ? "Aucun magasin à rattacher" : "Aucun lieu terrain"}</p>
            <p className="text-xs text-muted-ink">{kindFilter === "magasin" ? "Tous les magasins Reporting sont reliés à un spot." : "Aucun lieu de rue / porte-à-porte non rattaché."}</p>
          </div>
        ) : kindFilter === "terrain" ? (
          /* TERRAIN : lecture seule (jamais de rattachement magasin) */
          <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1" data-testid="unresolved-list-terrain">
            <div className="flex items-start gap-2 rounded-xl border border-[rgba(47,111,237,0.25)] bg-[rgba(47,111,237,0.06)] p-3 text-[12px] text-[#2F6FED]">
              <Route className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Ces lieux correspondent à une <b>ville seule</b> (rue, porte-à-porte ou mission itinérante). Ils ne sont <b>pas</b> rattachés à un magasin et sont comptés à part.</span>
            </div>
            {lieux.map((lieu) => (
              <div key={lieu.lieu_key} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-soft)] px-3 py-2" data-testid={`terrain-row-${lieu.lieu_key}`}>
                <div className="min-w-0">
                  <div className="break-words text-sm font-semibold">{lieu.label}</div>
                  <div className="text-[11px] text-muted-ink">{lieu.count} bulletin(s){lieu.rd_codes?.length ? ` · RD : ${lieu.rd_codes.join(", ")}` : ""}</div>
                </div>
                <Badge className="shrink-0 bg-[rgba(47,111,237,0.12)] text-[#2F6FED] hover:bg-[rgba(47,111,237,0.12)]">Terrain</Badge>
              </div>
            ))}
          </div>
        ) : (
          /* MAGASIN : cases à cocher + suggestion + choix manuel */
          <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1" data-testid="unresolved-list">
            {lieux.map((lieu) => {
              const target = hasTarget(lieu);
              return (
                <div key={lieu.lieu_key}
                  className={`rounded-xl border p-3 transition-colors ${checked[lieu.lieu_key] ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.05)]" : "border-[var(--border-soft)]"}`}
                  data-testid={`unresolved-row-${lieu.lieu_key}`}>
                  <div className="mb-2 flex items-start gap-2.5">
                    <Checkbox
                      checked={!!checked[lieu.lieu_key]}
                      disabled={!target}
                      onCheckedChange={(v) => setChecked((p) => ({ ...p, [lieu.lieu_key]: !!v }))}
                      className="mt-0.5"
                      data-testid={`sel-check-${lieu.lieu_key}`} />
                    <div className="min-w-0 flex-1">
                      <div className="break-words text-sm font-bold">{lieu.label}</div>
                      <div className="text-[11px] text-muted-ink">{lieu.count} bulletin(s){lieu.rd_codes?.length ? ` · RD : ${lieu.rd_codes.join(", ")}` : ""}</div>
                      {lieu.suggested_spot_id && !sel[lieu.lieu_key] && (
                        <div className="mt-0.5 text-[11px] text-[#0F5A2F]">Suggestion : <b>{lieu.suggested_name}</b> ({Math.round((lieu.score || 0) * 100)}%)</div>
                      )}
                      {!target && (
                        <div className="mt-0.5 text-[11px] text-[#8a6600]">Aucune suggestion — choisissez un spot pour l'activer.</div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pl-7">
                    <div className="min-w-[240px] flex-1">
                      <EntitySearch endpoint="/spots" placeholder="Choisir un spot…"
                        value={sel[lieu.lieu_key]} onChange={(v) => setSel((p) => ({ ...p, [lieu.lieu_key]: v }))}
                        labelFn={(sp) => sp.normalized_name} testId={`attach-spot-${lieu.lieu_key}`} icon={Store} />
                    </div>
                    <Button onClick={() => attach(lieu)} disabled={attaching === lieu.lieu_key || !target}
                      variant="outline" className="disabled:opacity-60" data-testid={`attach-btn-${lieu.lieu_key}`}>
                      {attaching === lieu.lieu_key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rattacher seul"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="unresolved-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewBulletinDialog({ open, onOpenChange, onSaved }) {
  const empty = { spot: null, rd: null, date: today(), heure: "", montant: "", statut: "Approuvé", frequence: "mensuel", genre: "", age: "", code_postal: "", ville: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty); }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.spot) { toast.error("Sélectionnez un spot"); return; }
    setSaving(true);
    try {
      await api.post("/bulletins", {
        spot_id: form.spot.id, rd_id: form.rd?.id || null, date: form.date, heure: form.heure || null,
        montant: form.montant || null, statut: form.statut, frequence: form.frequence,
        genre: form.genre || null, age: form.age || null, code_postal: form.code_postal || null, ville: form.ville || null,
      });
      toast.success("Bulletin enregistré");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-bulletin-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Nouveau bulletin</DialogTitle>
          <DialogDescription>Saisie manuelle d'un don recruté. Horodatage libre (heure optionnelle).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Spot *</Label>
              <EntitySearch endpoint="/spots" placeholder="Choisir un spot…" value={form.spot} onChange={(v) => set("spot", v)}
                labelFn={(s) => s.normalized_name} testId="nb-spot" icon={Store} full /></div>
            <div><Label className="text-[11px]">RD</Label>
              <EntitySearch endpoint="/rds" placeholder="Choisir un RD…" value={form.rd} onChange={(v) => set("rd", v)}
                labelFn={(r) => `${r.name}${r.code ? ` (${r.code})` : ""}`} testId="nb-rd" icon={User} full /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-[11px]">Date *</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1 h-9" data-testid="nb-date" /></div>
            <div><Label className="text-[11px]">Heure</Label><Input type="time" value={form.heure} onChange={(e) => set("heure", e.target.value)} className="mt-1 h-9" data-testid="nb-heure" /></div>
            <div><Label className="text-[11px]">Montant €</Label><Input type="number" value={form.montant} onChange={(e) => set("montant", e.target.value)} placeholder="15" className="mt-1 h-9" data-testid="nb-montant" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Statut</Label>
              <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="nb-statut"><SelectValue /></SelectTrigger>
                <SelectContent>{BULLETIN_STATUTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-[11px]">Fréquence</Label>
              <Select value={form.frequence} onValueChange={(v) => set("frequence", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="nb-freq"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mensuel">Mensuel</SelectItem><SelectItem value="ponctuel">Ponctuel</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div><Label className="text-[11px]">Genre</Label>
              <Select value={form.genre || "none"} onValueChange={(v) => set("genre", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="nb-genre"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="H">H</SelectItem><SelectItem value="F">F</SelectItem><SelectItem value="Autre">Autre</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-[11px]">Âge</Label><Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="mt-1 h-9" data-testid="nb-age" /></div>
            <div><Label className="text-[11px]">Code postal</Label><Input value={form.code_postal} onChange={(e) => set("code_postal", e.target.value)} className="mt-1 h-9" data-testid="nb-cp" /></div>
            <div><Label className="text-[11px]">Ville</Label><Input value={form.ville} onChange={(e) => set("ville", e.target.value)} className="mt-1 h-9" data-testid="nb-ville" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="nb-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="nb-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntitySearch({ endpoint, placeholder, value, onChange, labelFn, testId, icon: Icon, full }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const { data } = await api.get(endpoint, { params: { search: q || undefined, limit: 20 } });
      setResults(data);
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, endpoint]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`h-9 justify-start border-[var(--border-soft)] ${full ? "mt-1 w-full" : "w-52"}`} data-testid={testId}>
          {Icon && <Icon className="mr-1.5 h-3.5 w-3.5 text-muted-ink" />}
          <span className="truncate text-xs">{value ? labelFn(value) : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} value={q} onValueChange={setQ} data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>Aucun résultat.</CommandEmpty>
            <CommandGroup>
              {value && <CommandItem value="__clear" onSelect={() => { onChange(null); setOpen(false); }} className="text-muted-ink">Effacer la sélection</CommandItem>}
              {results.map((r) => (
                <CommandItem key={r.id} value={r.id} onSelect={() => { onChange(r); setOpen(false); }} data-testid={`${testId}-opt-${r.id}`}>
                  <span className="truncate text-xs font-semibold">{labelFn(r)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
